import React, { useState, useEffect } from 'react';
import fileDownload from 'js-file-download';

import {
  Box,
  Button,
  Input,
  Card,
  CardHeader,
  List,
  ListItem,
  Typography,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';

import returnDate from '../../../utils/dateTime';
import { IFileInfo } from '../../../types/reports';
import { Modal, Loader } from '../../../global';

const StatsUpload = () => {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();

  const [statsFiles, setStatsFiles] = useState<{
    hNode: IFileInfo[];
    stats: IFileInfo[];
  }>({ hNode: [], stats: [] });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    titleText: '',
    bodyText: '',
    autoClose: false,
  });

  const [fileTypeDisplay, setFileTypeDisplay] = React.useState('stats');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosPrivate.get('/files/stats');
        setStatsFiles(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [axiosPrivate]);

  //   Utility function to trigger the modal
  const triggerModal = (title: string, body: string, autoClose: boolean) => {
    setModalInfo({
      titleText: title,
      bodyText: body,
      autoClose,
    });
    setShowModal(true);
    setLoading(false);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fileType: string
  ) => {
    setLoading(true);

    const file = event.target.files?.[0];
    // Clear the file input value
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (file) {
      // Multer is expecting a form data object with the file and the fileType
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', fileType);
      try {
        const response = await axiosPrivate.post('/upload/stats', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        if (response && response.status === 200) {
          // Update the state with the new file
          setStatsFiles((prevFileInfos) => {
            const filteredFileInfos = prevFileInfos[
              fileType as keyof typeof prevFileInfos
            ].filter((existingFile) => existingFile.url !== response.data.url);
            return {
              ...prevFileInfos,
              [fileType]: [...filteredFileInfos, response.data],
            };
          });
          triggerModal(
            'Success',
            `File ${file.name}  uploaded Correctly`,
            true
          );
        } else {
          triggerModal(
            'Error Uploading File',
            `Error uploading ${file.name}`,
            true
          );
        }
      } catch (error) {
        const serverError = error as { response: { data: { error: string } } };
        if (serverError.response) {
          console.error(serverError.response.data.error);
        }
        triggerModal(
          `Error: ${serverError.response.data.error}`,
          `Error uploading ${file.name}`,
          false
        );
      }
    }
  };

  const handleFileDownload = async (fileName: string) => {
    try {
      const fileDataResponse = await axiosPrivate.post(
        '/download/stats',
        {
          fileName,
          filetype: fileTypeDisplay,
        },
        {
          responseType: 'blob',
        }
      );

      if (fileDataResponse.status === 200) {
        fileDownload(fileDataResponse.data as unknown as File, fileName);
      } else {
        console.error(fileDataResponse);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Dynamically generate the list of files based on toggle buttons
  const generateFileList = () => {
    const files =
      fileTypeDisplay === 'stats' ? statsFiles.stats : statsFiles.hNode;

    if (!files) return null;

    return files.map((file) => (
      <Box
        sx={{ borderTop: `1px solid ${grey[200]}` }}
        key={`${file.name}-${file.date}`}
      >
        <ListItem component="div" sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="subtitle2"
            sx={{
              alignSelf: 'start',
              width: '50%',
              wordWrap: 'break-word',
            }}
          >
            {file.name}
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              color="black"
              size="small"
              sx={{ mr: theme.spacing(1), backgroundColor: 'green' }}
              onClick={() => {
                handleFileDownload(file.name);
              }}
            >
              Download
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'end' }}>
              {returnDate(file.date)}
            </Typography>
          </Box>
        </ListItem>
      </Box>
    ));
  };
  if (loading) return <Loader />;

  return (
    <>
      {showModal && (
        <Modal
          titleText={modalInfo.titleText}
          bodyText={modalInfo.bodyText}
          autoClose={modalInfo.autoClose}
          updateParent={setShowModal}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box>
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{
              maxWidth: '300px',
              margin: '1em',
              backgroundColor: 'green',
              ':hover': {
                backgroundColor: 'green',
              },
            }}
          >
            Upload Stats file
            <Input
              sx={{
                clip: 'rect(0 0 0 0)',
                clipPath: 'inset(50%)',
                height: 1,
                overflow: 'hidden',
                position: 'absolute',
                bottom: 0,
                left: 0,
                whiteSpace: 'nowrap',
                width: 1,
              }}
              type="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileUpload(e, 'stats')
              }
            />
          </Button>
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
            sx={{ maxWidth: '300px', margin: '1em' }}
          >
            Upload hNode file
            <Input
              sx={{
                clip: 'rect(0 0 0 0)',
                clipPath: 'inset(50%)',
                height: 1,
                overflow: 'hidden',
                position: 'absolute',
                bottom: 0,
                left: 0,
                whiteSpace: 'nowrap',
                width: 1,
              }}
              type="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFileUpload(e, 'hNode')
              }
            />
          </Button>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Card variant="outlined" sx={{ width: '800px' }}>
            <CardHeader
              title="Previous Stats Files"
              sx={{
                bgcolor: grey[100],
                borderBottom: `1px solid ${grey[200]}`,
                width: '100%',
                margin: '0 auto 0',
                textAlign: 'center', // Add this line to center the text
              }}
            />
            <RadioGroup
              row
              aria-labelledby="demo-form-control-label-placement"
              name="position"
              sx={{ justifyContent: 'center' }}
              value={fileTypeDisplay}
              onChange={(e) =>
                setFileTypeDisplay((e.target as HTMLInputElement).value)
              }
            >
              <FormControlLabel
                value="stats"
                control={<Radio />}
                label="Stats"
                labelPlacement="start"
              />
              <FormControlLabel
                value="hNode"
                control={<Radio />}
                label="hNode"
                labelPlacement="start"
              />
            </RadioGroup>
            <List sx={{ width: '800px' }}>{generateFileList()}</List>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default StatsUpload;
